"use client";

import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface SendResult {
  creatorId?: number;
  creatorName: string;
  creatorPhotoUrl?: string | null;
  success: boolean;
  error?: string;
}

interface EmailSendState {
  isActive: boolean;
  scriptId?: string;
  total: number;
  current: number;
  sent: number;
  failed: number;
  results: SendResult[];
  startTime?: Date;
  isComplete: boolean;
}

interface EmailSendContextType {
  sendState: EmailSendState;
  startSend: (scriptId: string | undefined, total: number) => void;
  completeSend: (sent: number, failed: number, results: SendResult[]) => void;
  dismiss: () => void;
}

const initialState: EmailSendState = {
  isActive: false,
  total: 0,
  current: 0,
  sent: 0,
  failed: 0,
  results: [],
  isComplete: false,
};

const EmailSendContext = createContext<EmailSendContextType | undefined>(undefined);

export function EmailSendProvider({ children }: { children: ReactNode }) {
  const [sendState, setSendState] = useState<EmailSendState>(initialState);

  const startSend = useCallback((scriptId: string | undefined, total: number) => {
    setSendState({
      isActive: true,
      scriptId,
      total,
      current: 0,
      sent: 0,
      failed: 0,
      results: [],
      startTime: new Date(),
      isComplete: false,
    });
  }, []);

  const completeSend = useCallback((sent: number, failed: number, results: SendResult[]) => {
    setSendState(prev => ({
      ...prev,
      sent,
      failed,
      current: prev.total,
      results,
      isComplete: true,
    }));
  }, []);

  const dismiss = useCallback(() => {
    setSendState(initialState);
  }, []);

  return (
    <EmailSendContext.Provider value={{ sendState, startSend, completeSend, dismiss }}>
      {children}
    </EmailSendContext.Provider>
  );
}

export function useEmailSend() {
  const context = useContext(EmailSendContext);
  if (context === undefined) {
    throw new Error("useEmailSend must be used within an EmailSendProvider");
  }
  return context;
}
